import React, { useEffect, useState, useMemo } from "react";
import { Trash2, Edit, Search } from "lucide-react";
import axios from "axios";
import Apiurl from "../../environment/Apiurl";

function Department() {
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);

  // Form state for adding
  const [addData, setAddData] = useState({
    designation: "",
    department: "",
  });

  // Form state for editing
  const [updateData, setUpdateData] = useState({
    designation: "",
    department: "",
  });

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId); // Debugging
      if (userRoleId === "unknown") {
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setLoadingPrivileges(false);
        return;
      }
      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data); // Debugging
      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData); // Debugging
        const privileges = {};
        roleData.pages.forEach((page) => {
          privileges[page.page] = {
            access: page.access,
            ...page.priviallages, // Keep original spelling to match API
          };
        });
        console.log("Set user privileges:", privileges); // Debugging
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
      }
    } catch (error) {
      console.error("Error fetching user privileges:", error.message);
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

      useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

  // Check if user has specific permission for Department page
  const hasPermission = (permission) => {
    if (!userPrivileges || !userPrivileges["Department"]) {
      console.log("No user privileges or Department page data found"); // Debugging
      return false;
    }
    const privileges = userPrivileges["Department"];
    console.log(
      `Checking permission '${permission}' for Department:`,
      privileges
    ); // Debugging
    if (permission === "access") {
      return privileges.access === true;
    }
    if (permission in privileges && privileges[permission] === false) {
      console.log(`Permission '${permission}' explicitly denied`); // Debugging
      return false;
    }
    const hasPerm =
      privileges.access && (privileges.all || privileges[permission]);
    console.log(`Permission '${permission}' result:`, hasPerm); // Debugging
    return hasPerm;
  };

  
// Fetch department data
const fetchDepartmentData = async () => {
  if (!hasPermission("read")) {
    console.warn("Read permission denied for Department page"); // Debugging
    setError("You do not have permission to view departments.");
    return;
  }
  setLoading(true);
  try {
    const response = await axios.get(`${Apiurl}/department`);
    console.log("Department data fetched:", response.data); // Debugging

    const departmentData = Array.isArray(response.data)
      ? response.data
      : response.data
      ? [response.data]
      : [];

    // Reverse the department data
    departmentData.reverse();

    setResponseData(departmentData);
    setError(null);
  } catch (error) {
    console.error("Error fetching departments:", error.message);
    setError("Failed to fetch departments. Please try again.");
    setResponseData([]);
    setTimeout(() => setError(null), 3000);
  } finally {
    setLoading(false);
  }
};


  // Handle input changes for add form
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Add form input changed: ${name} = ${value}`); // Debugging
    setAddData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle input changes for edit form
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Edit form input changed: ${name} = ${value}`); // Debugging
    setUpdateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validate form data
  const validateForm = (data) => {
    if (!data.designation) return "Designation is required.";
    if (!data.department) return "Department is required.";
    return null;
  };

  // Handle add form submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Department page"); // Debugging
      setError("You do not have permission to add departments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(addData);
    if (validationError) {
      console.warn("Validation error:", validationError); // Debugging
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const formData = {
        designation: addData.designation,
        department: addData.department,
        speciality: addData.speciality,
      };
      console.log("Adding department with data:", formData); // Debugging
      await axios.post(`${Apiurl}/department`, formData);
      setAddData({
        designation: "",
        department: "",
      });
      fetchDepartmentData();
      closeAddModal();
    } catch (error) {
      console.error("Error adding department:", error.message);
      setError("Failed to add department. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Department page"); // Debugging
      setError("You do not have permission to edit departments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(updateData);
    if (validationError) {
      console.warn("Validation error:", validationError); // Debugging
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log(
        "Updating department with ID:",
        editingId,
        "Data:",
        updateData
      ); // Debugging
      await axios.put(`${Apiurl}/department/${editingId}`, updateData);
      fetchDepartmentData();
      closeEditModal();
    } catch (error) {
      console.error("Error updating department:", error.message);
      setError("Failed to update department. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete department
  const deleteDepartment = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Department page"); // Debugging
      setError("You do not have permission to delete departments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid department ID:", deleteId); // Debugging
      setError("Invalid department ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting department with ID:", deleteId); // Debugging
      await axios.delete(`${Apiurl}/department/${deleteId}`);
      fetchDepartmentData();
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting department:", error.message);
      setError("Failed to delete department. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Edit department
  const handleEdit = (id, item) => {
    if (hasPermission("update")) {
      console.log("Opening Edit Department modal for ID:", id, "Item:", item); // Debugging
      setEditingId(id);
      setUpdateData({
        designation: item.designation || "",
        department: item.department || "",
      });
      setShowEditModal(true);
    } else {
      console.warn("Update permission denied for Department page"); // Debugging
      setError("You do not have permission to edit departments.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (hasPermission("delete")) {
      if (!id) {
        console.warn("Invalid department ID:", id); // Debugging
        setError("Invalid department ID.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      console.log("Opening Delete Department modal for ID:", id); // Debugging
      setDeleteId(id);
      setShowDeleteModal(true);
    } else {
      console.warn("Delete permission denied for Department page"); // Debugging
      setError("You do not have permission to delete departments.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing Delete Department modal"); // Debugging
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Open add modal
  const openAddModal = () => {
    if (hasPermission("create")) {
      console.log("Opening Add Department modal"); // Debugging
      setShowAddModal(true);
    } else {
      console.warn("Create permission denied for Department page"); // Debugging
      setError("You do not have permission to add departments.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Close add modal
  const closeAddModal = () => {
    console.log("Closing Add Department modal"); // Debugging
    setShowAddModal(false);
    setAddData({
      designation: "",
      department: "",
    });
    setError(null);
  };

  // Close edit modal
  const closeEditModal = () => {
    console.log("Closing Edit Department modal"); // Debugging
    setShowEditModal(false);
    setEditingId(null);
    setUpdateData({
      designation: "",
      department: "",
    });
    setError(null);
  };

  useEffect(() => {
    console.log("Fetching initial privileges..."); // Debugging
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (hasPermission("access")) {
      console.log("Access granted for Department page"); // Debugging
      if (hasPermission("read")) {
        console.log("Fetching department data due to read permission"); // Debugging
        fetchDepartmentData();
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Department page"); // Debugging
      setError("You do not have permission to access this page.");
    }
  }, [userPrivileges, loadingPrivileges]);

  // Filter data with useMemo
  const filteredData = useMemo(() => {
    return responseData.filter(
      (item) =>
        (item.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.designation?.toLowerCase().includes(searchTerm.toLowerCase())) ??
        // item.speciality?.toLowerCase().includes(searchTerm.toLowerCase())) ??
        false
    );
  }, [responseData, searchTerm]);

  // Update items per page when showEntries changes
  useEffect(() => {
    console.log("Updating items per page to:", showEntries); // Debugging
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
    setCurrentPage(1);
  }, [showEntries, filteredData]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page); // Debugging
      setCurrentPage(page);
    }
  };

  if (loadingPrivileges) {
    return <div className="p-3">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Department page"
    ); // Debugging
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="text-end">
        {hasPermission("create") && (
          <button
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#01AEBE" }}
            onClick={openAddModal}
          >
            + Add Department
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="bg-white rounded shadow-sm">
            <div className="row mb-3 crm-table-toolbar">
              <div className="col-md-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-table"></i>
                  </span>
                  <select
                    className="form-select"
                    value={showEntries}
                    onChange={(e) => {
                      console.log(
                        "Changing items per page to:",
                        e.target.value
                      ); // Debugging
                      setShowEntries(e.target.value);
                    }}
                  >
                    <option value="All">Show All</option>
                    {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        Show {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-7"></div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control pe-5"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => {
                        console.log("Search term changed to:", e.target.value); // Debugging
                        setSearchTerm(e.target.value);
                      }}
                      style={{ width: "250px", paddingRight: "2.5rem" }}
                    />
                    <Search
                      size={16}
                      className="position-absolute text-muted"
                      style={{
                        right: "10px",
                        top: "30%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}

            <div className="table-responsive">
              <table className="table table-hover">
                <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                  <tr>
                    <th>S.No</th>
                    <th>Department</th>
                    <th>Designation</th>
                    {(hasPermission("update") || hasPermission("delete")) && (
                      <th>Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr key={item._id || index} className="align-middle">
                        <td>{indexOfFirstItem + index + 1}</td>

                        <td>{item.department || "-"}</td>
                        <td>{item.designation || "-"}</td>
                        {(hasPermission("update") ||
                          hasPermission("delete")) && (
                          <td>
                            {hasPermission("delete") && (
                              <button
                                className="btn btn-sm p-1 border-0"
                                style={{ backgroundColor: "transparent" }}
                                onClick={() => openDeleteModal(item._id)}
                              >
                                <Trash2 size={16} className="text-danger" />
                              </button>
                            )}
                            {hasPermission("update") && (
                              <button
                                className="btn btn-sm p-1 border-0"
                                style={{ backgroundColor: "transparent" }}
                                onClick={() => handleEdit(item._id, item)}
                              >
                                <Edit size={16} className="text-primary" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          hasPermission("update") || hasPermission("delete")
                            ? 5
                            : 4
                        }
                        className="text-center py-3"
                      >
                        {loading ? "Loading..." : "No departments found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="container">
                <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      currentPage > 1 && setCurrentPage(currentPage - 1)
                    }
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>

                  <div
                    className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {pages.map((page, index) => (
                      <button
                        key={index}
                        className={`btn btn-sm px-3 flex-shrink-0 ${
                          page === currentPage
                            ? "text-white"
                            : "btn-outline-secondary"
                        }`}
                        style={
                          page === currentPage
                            ? { backgroundColor: "#20c997", border: "none" }
                            : {}
                        }
                        onClick={() => handlePageClick(page)}
                        disabled={page === "..."}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      currentPage < totalPages &&
                      setCurrentPage(currentPage + 1)
                    }
                    disabled={currentPage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addDepartmentModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6
                  className="modal-title text-dark"
                  id="addDepartmentModalLabel"
                >
                  Add Department
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="department"
                          className="form-label fw-bold"
                        >
                          Department
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="department"
                          name="department"
                          placeholder="Enter Department"
                          value={addData.department}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="designation"
                          className="form-label fw-bold"
                        >
                          Designation
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="designation"
                          name="designation"
                          placeholder="Enter Designation"
                          value={addData.designation}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeAddModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-sm px-4"
                      style={{ backgroundColor: "#01AEBE" }}
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {hasPermission("update") && (
        <div
          className={`modal fade ${showEditModal ? "show" : ""}`}
          style={{ display: showEditModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="editDepartmentModalLabel"
          aria-hidden={!showEditModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6
                  className="modal-title text-dark"
                  id="editDepartmentModalLabel"
                >
                  Edit Department
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="department"
                          className="form-label fw-bold"
                        >
                          Department
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="department"
                          name="department"
                          placeholder="Enter Department"
                          value={updateData.department}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="designation"
                          className="form-label fw-bold"
                        >
                          Designation
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="designation"
                          name="designation"
                          placeholder="Enter Designation"
                          value={updateData.designation}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeEditModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-sm px-4"
                      style={{ backgroundColor: "#01AEBE" }}
                    >
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteDepartmentModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteDepartmentModalLabel">
                  Delete Department
                </h5>
                <p style={{ marginTop: "10px" }}>
                  You're going to delete this department. Are you sure?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={deleteDepartment}
                >
                  Yes, Delete It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || showEditModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default Department;
